import { useMergedRef } from '@mantine/hooks'
import Hls from 'hls.js'
import { noop } from 'lodash-es'
import type { VideoHTMLAttributes } from 'react'
import { forwardRef, useEffect, useRef } from 'react'
import Empty from '../Empty'
import Text from '../ui/Text'

type HlsStreamProps = VideoHTMLAttributes<HTMLVideoElement> & {
  src: string
  onLoaded?: () => void
  /** Fired on unsupported runtime / fatal hls.js error. The native
   *  <video> `error` event doesn't fire in these cases. */
  onFatalError?: () => void
}

const HlsStream = forwardRef(({ src, ...props }: HlsStreamProps, ref) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const mergedVideoRef = useMergedRef(videoRef, ref)
  const hlsRef = useRef<Hls | null>(null)
  const errorRef = useRef<string | null>(null)

  // Via ref so an unstable callback doesn't re-init hls.js.
  const onFatalErrorRef = useRef(props.onFatalError)
  onFatalErrorRef.current = props.onFatalError

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    function load() {
      props.onLoaded?.()
      // A workaround for autoplay in some Chrome browsers.
      // There's a bug of React that the `muted` attribute are not being
      // rendered properly. Thus, as one possible reason, some Chrome browsers
      // with a strict policy may not allow autoplay. So we explicitly call
      // `play()` during `loadeddata` events.
      if (props.autoPlay) {
        // Suppress error on playback failure.
        video?.play().catch(noop)
      }
    }

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.addEventListener('loadeddata', load)
      video.src = src
      return () => {
        video.removeEventListener('loadeddata', load)
      }
    }

    if (Hls.isSupported()) {
      video.addEventListener('loadeddata', load)

      hlsRef.current = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 0,
      })

      hlsRef.current.loadSource(src)
      hlsRef.current.attachMedia(video)

      hlsRef.current.on(Hls.Events.MANIFEST_PARSED, () => {
        if (props.autoPlay) {
          video.play().catch(noop)
        }
      })

      hlsRef.current.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hlsRef.current?.startLoad()
              break
            case Hls.ErrorTypes.MEDIA_ERROR:
              hlsRef.current?.recoverMediaError()
              break
            default:
              hlsRef.current?.destroy()
              onFatalErrorRef.current?.()
              break
          }
        }
      })
    } else {
      errorRef.current = 'common:error.hls-not-supported'
      props.onLoaded?.()
      onFatalErrorRef.current?.()
    }

    return () => {
      video.removeEventListener('loadeddata', load)

      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
    }
  }, [src, props.autoPlay, props.onLoaded])

  return errorRef.current ? (
    <Empty
      className="size-full justify-center text-sm"
      imageClassName="h-auto"
      description={<Text i18nKey={errorRef.current} />}
    />
  ) : (
    <video
      ref={mergedVideoRef}
      className="size-full"
      style={{ maxWidth: '100%', ...props.style }}
      {...props}
    />
  )
})

export default HlsStream
